import React from 'react';
import { Box, Container, Grid, Typography, useTheme } from '@mui/material';
import Card from '../ui/Card';

export const CorporateEvolutionSection: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const timeline = [
    {
      year: "'15",
      title: 'Inception',
      desc: 'Established focusing on core structural execution and specialized contracting services, building a foundation of technical rigor.',
      active: false,
    },
    {
      year: "'18",
      title: 'Expansion',
      desc: 'Expanded portfolio to include comprehensive civil works, MEP integration, and large-scale residential developments.',
      active: false,
    },
    {
      year: "'24",
      title: 'Incorporation',
      desc: 'Formalized as Inverika Enterprises Private Limited, consolidating operations and launching the dedicated manufacturing control facility.',
      active: true,
      verified: true,
    },
  ];

  return (
    <Box
      id="evolution"
      sx={{
        py: { xs: 10, md: 14 },
        backgroundColor: isDark ? 'rgba(22, 29, 47, 0.4)' : '#f8fafc',
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              color: theme.palette.text.primary,
              fontFamily: "'BankGothic Md BT', 'BankGothic MD BT', 'Space Grotesk', sans-serif",
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              mb: 1.5,
            }}
          >
            CORPORATE EVOLUTION
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: 640,
              mx: 'auto',
              fontSize: '1rem',
            }}
          >
            Tracing our trajectory from specialized contractors to an integrated engineering enterprise.
          </Typography>
        </Box>

        <Box sx={{ position: 'relative' }}>
          {/* Horizontal Connecting Line */}
          <Box
            sx={{
              position: 'absolute',
              top: '64px',
              left: '10%',
              right: '10%',
              height: '1px',
              backgroundColor: theme.palette.divider,
              display: { xs: 'none', md: 'block' },
              zIndex: 0,
            }}
          />

          <Grid container spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
            {timeline.map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderTop: item.active ? `3px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                    boxShadow: isDark ? 'none' : '0 8px 30px rgba(0, 0, 0, 0.03)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  {/* Year Badge */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.5,
                      backgroundColor: item.active
                        ? theme.palette.primary.main
                        : isDark
                        ? 'rgba(0, 102, 255, 0.15)'
                        : 'rgba(0, 80, 203, 0.08)',
                      color: item.active ? '#ffffff' : theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '1rem',
                      fontFamily: "'BankGothic Md BT', 'BankGothic MD BT', 'Space Grotesk', sans-serif",
                      mb: 3,
                    }}
                  >
                    {item.year}
                  </Box>

                  {/* Card Title */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: theme.palette.text.primary,
                        fontFamily: 'Space Grotesk',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                      }}
                    >
                      {item.title}
                    </Typography>

                    {item.verified && (
                      <span className="material-symbols-outlined" style={{ fontSize: 20, color: theme.palette.primary.main }}>
                        verified
                      </span>
                    )}
                  </Box>

                  {/* Card Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.65,
                      fontSize: '0.9375rem',
                    }}
                  >
                    {item.desc}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default CorporateEvolutionSection;
